import React, { useState, useEffect, useCallback } from 'react';
import { withRouter, NavLink } from 'react-router-dom';
import moment from 'moment';
import PerfectScrollbar from 'react-perfect-scrollbar';
import 'react-perfect-scrollbar/dist/css/styles.css';
import { Card, Row, Col } from 'reactstrap';

import { newFetch, GET } from '../../../../helpers/fetch';
import { config } from '../../../../config/index';
import placeholderImage from '../../../../assets/images/placeholder.jpg';
import ToastUtil from '../../../../components/ToastNotification/ToastUtil';

const { SERVER_URL } = config;

const FeedbackDetail = ({
  history,
  match: {
    params: { feedback_id },
  },
}) => {
  const user = JSON.parse(localStorage.getItem('user'));

  const [feedback, setFeedback] = useState(null);
  const [messages, setMessages] = useState([]);
  const [curMessage, setCurMessage] = useState('');
  const [messageBox, setMessageBox] = useState(null);

  // Fetch feedback + messages
  const fetchFeedback = async () => {
    const res = await newFetch(GET, `/feedback/${feedback_id}`);

    if (res.success) {
      setFeedback(res.data);
      if (res.data.messages) setMessages(res.data.messages);
    }
  };

  // Fetch chat messages only
  const fetchMessages = async () => {
    const res = await newFetch(GET, `/feedback-message/${feedback_id}`);
    if (res.success) {
      setMessages(res.data);
    }
  };

  // Scroll to bottom automatically
  const scrollToBottom = useCallback(() => {
    if (messageBox) messageBox.scrollTop = messageBox.scrollHeight + 999;
  }, [messageBox]);

  useEffect(() => {
    if (messages.length > 0) scrollToBottom();
  }, [messages, scrollToBottom]);

  useEffect(() => {
    fetchFeedback();
    fetchMessages();
  }, [feedback_id]);

  // Send message
  const addMessage = async () => {
    if (!curMessage.trim()) return;

    const body = {
      feedback_id,
      message: curMessage,
      type: user.type,
      added_by: user.id,
    };

    const res = await newFetch('POST', '/feedback-message', body);

    if (res.success) {
      setCurMessage('');
      fetchMessages(); // reload updated messages
    } else {
      ToastUtil.error(res.message || 'Failed to send message');
    }
  };

  const onKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      addMessage();
    }
  };

  return (
    <React.Fragment>
      <div style={{ paddingTop: '3rem', marginBottom: '6rem' }}>
        {/* Header */}
        <div
          className="d-flex flex-column"
          style={{ paddingLeft: '3rem', paddingRight: '3rem' }}
        >
          <NavLink
            to="/communication"
            className="text-decoration-none"
            style={{ color: 'inherit' }}
          >
            <div style={{ color: '#A6478E' }}>
              <i className="fas fa-chevron-left pe-2"></i> Back to Communication
            </div>
          </NavLink>

          <div className="d-flex justify-content-between pt-4">
            <h5>Feedback ID: {feedback ? feedback.feedback_id : ''}</h5>
          </div>
        </div>

        {/* Feedback Content */}
        <div
          className="d-flex"
          style={{ marginTop: '2rem', marginLeft: '3rem', marginRight: '3rem' }}
        >
          {/* Left side */}
          <div className="flex-grow-1">
            <div className="rounded shadow bg-white">
              {feedback && (
                <div className="p-4 d-flex flex-column">
                  <h6 style={{ wordBreak: 'break-all' }}>{feedback.title}</h6>
                  <p className="pt-2" style={{ wordBreak: 'break-all' }}>
                    {feedback.description}
                  </p>

                  {/* Files */}
                  {feedback.uploads && feedback.uploads.length > 0 && (
                    <>
                      <div className="pt-4">Attached File(s)</div>
                      <div className="pt-3 row" style={{ maxWidth: '100%' }}>
                        {feedback.uploads.map((upload) => (
                          <div
                            key={upload.id}
                            className="col-3"
                            style={{ marginBottom: '30px' }}
                          >
                            <div
                              className="p-3 h-100 border rounded"
                              onClick={() =>
                                window.open(
                                  `${SERVER_URL}/${upload.url}`,
                                  '_blank'
                                )
                              }
                              style={{ cursor: 'pointer' }}
                            >
                              {['jpg', 'jpeg', 'png'].includes(
                                upload.url.split('.').pop().toLowerCase()
                              ) ? (
                                <img
                                  src={`${SERVER_URL}/${upload.url}`}
                                  alt=""
                                  style={{ width: '100%' }}
                                />
                              ) : upload.url.split('.').pop().toLowerCase() ===
                                'pdf' ? (
                                <div className="text-center">PDF File</div>
                              ) : (
                                ''
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Sidebar */}
          {feedback && (
            <div
              className="d-flex flex-column"
              style={{ minWidth: '250px', width: '250px', marginLeft: '2rem' }}
            >
              <strong>Date Created</strong>
              <div className="mt-2">
                {moment(feedback.createdAt).format('DD MMM YYYY, h:mma')}
              </div>

              <strong className="mt-4">Created by</strong>
              <div
                className="d-flex align-items-center my-1 py-1 mt-2 rounded"
                style={{ backgroundColor: '#f1f1f1' }}
              >
                <div className="mx-2">
                  <img
                    className="img-responsive rounded-circle border"
                    src={
                      feedback.associate.image
                        ? `${SERVER_URL}/${feedback.associate.image}`
                        : placeholderImage
                    }
                    alt="username-img"
                    style={{ height: '35px', width: '35px' }}
                  />
                </div>
                <div className="d-flex justify-content-between align-items-center w-100">
                  <div>
                    {`${feedback.associate.first_name} ${feedback.associate.last_name}`}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chat Messages */}
        <div
          className="d-flex"
          style={{
            marginTop: '2rem',
            marginLeft: '3rem',
            marginRight: '3rem',
          }}
        >
          {/* Chat left */}
          <div className="flex-grow-1">
            <div className="w-100 user-chat mt-4 mt-sm-0">
              <Card>
                <PerfectScrollbar
                  className="chat-conversation p-3 px-2"
                  style={{ height: '600px' }}
                  containerRef={(ref) => setMessageBox(ref)}
                >
                  <ul
                    className="list-unstyled mb-0"
                    style={
                      messages.length === 0
                        ? {
                            height: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                          }
                        : {}
                    }
                  >
                    {messages.length === 0 && (
                      <div
                        style={{
                          color: '#999',
                          fontSize: '14px',
                        }}
                      >
                        Send a message to start the conversation
                      </div>
                    )}
                    <br />
                    {messages.map((msg) => (
                      <li
                        key={'msg_' + msg.id}
                        className={msg.added_by === user.id ? 'right' : ''}
                      >
                        <div className="conversation-list">
                          <div className="conversation-item">
                            <div className="conversation-name">
                              <span className="user-name">
                                {msg.sender
                                  ? `${msg.sender.first_name} ${msg.sender.last_name}`
                                  : 'User'}
                              </span>

                              <span className="time">
                                {moment(msg.createdAt).format(
                                  'DD/MM/YYYY hh:mm A'
                                )}
                              </span>
                            </div>

                            <div className="ctext-wrap">
                              <div className="ctext-wrap-content">
                                <p className="mb-0">{msg.message}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </li>
                    ))}
                  </ul>
                </PerfectScrollbar>

                {/* Input */}
                <div className="p-3">
                  <div className="mb-2">
                    <label className="fw-bold">Message</label>
                  </div>

                  <textarea
                    rows={5}
                    value={curMessage}
                    onKeyPress={onKeyPress}
                    onChange={(e) => setCurMessage(e.target.value)}
                    className="form-control border bg-light-subtle"
                    style={{ width: '100%' }}
                  ></textarea>

                  <div className="d-flex justify-content-end mt-3">
                    <button
                      onClick={addMessage}
                      className="btn btn-brand adjust-fs adjust-shadow"
                      style={{ width: '8rem' }}
                    >
                      Submit
                    </button>
                  </div>
                </div>
              </Card>
            </div>
          </div>

          {/* Empty right space */}
          <div
            style={{ minWidth: '250px', width: '250px', marginLeft: '2rem' }}
          />
        </div>
      </div>
    </React.Fragment>
  );
};

export default withRouter(FeedbackDetail);
